import React from "react";
import { Link, NavLink } from "react-router-dom";
import "../lot11b-legal.css";

// Lot 11D — informations publiques. Ne pas publier commercialement avant
// identification de l'éditeur, canal de contact et vérification juridique.
const updated = "28 octobre 2026";
const pages = {
  privacy: {
    eyebrow: "Protection des données",
    title: "Politique de confidentialité",
    intro: "Découvrez quelles informations nous utilisons, pourquoi elles sont nécessaires et comment elles accompagnent votre expérience ProScore Live.",
    sections: [
      ["Les données de votre compte", [
        "Lors de la création d'un compte, nous utilisons votre adresse e-mail, un identifiant de compte et les informations nécessaires à l'authentification. Ces données permettent de vous connecter, de retrouver votre espace personnel et de protéger votre accès.",
        "Si vous choisissez la connexion Google, les informations de profil autorisées par Google sont utilisées pour cette connexion. ProScore Live n'obtient pas, par ce seul biais, l'accès à votre messagerie ou à vos fichiers Google."
      ]],
      ["L'accès aux fonctionnalités premium", [
        "Nous associons à votre compte les droits d'accès, leurs périodes de validité et les informations de commande nécessaires au contrôle des fonctionnalités réservées. Un paiement en attente ou une simple connexion ne donne pas automatiquement accès aux contenus premium.",
        "Les informations de paiement traitées par un éventuel prestataire d'encaissement relèvent aussi de ses propres conditions et de sa politique de confidentialité."
      ]],
      ["À quoi servent ces informations ?", [
        "Vos données de compte servent à gérer l'authentification, les droits d'accès, la sécurité et, si nécessaire, l'assistance. Les données relatives aux matchs, aux compétitions et aux analyses servent à afficher le contenu du service."
      ]],
      ["Les prestataires techniques", [
        "L'authentification et certaines données de compte sont gérées avec Supabase. Google intervient uniquement si vous sélectionnez sa méthode de connexion. L'application s'appuie également sur des services d'hébergement, de données sportives et de traitement d'analyses.",
        "L'utilisation de ces prestataires peut impliquer des traitements dans différents pays, selon leurs infrastructures et leurs engagements contractuels."
      ]],
      ["La sécurité de votre compte", [
        "Les accès réservés sont vérifiés côté serveur et des mécanismes d'authentification protègent votre compte. Aucun service en ligne ne pouvant garantir une sécurité absolue, nous vous recommandons de conserver vos identifiants confidentiels et d'utiliser un mot de passe unique.",
        "Ne communiquez jamais votre mot de passe ni vos codes de connexion à une personne se présentant comme membre de l'assistance."
      ]],
      ["Conservation des données et vos droits", [
        "Les données sont conservées selon leur utilité pour le fonctionnement du service, la sécurité et les obligations applicables. Les durées précises et les modalités de suppression doivent être confirmées pour chaque catégorie avant l'ouverture commerciale.",
        "Vous pouvez demander des informations sur les données associées à votre compte et, selon les règles applicables, leur accès, leur rectification ou leur suppression. Le canal destiné à ces demandes sera précisé avec les coordonnées officielles de l'éditeur."
      ]],
      ["Cookies et stockage local", [
        "La connexion peut utiliser des informations conservées par le navigateur. Un stockage de session est également utilisé pendant le parcours de récupération du mot de passe.",
        "Pour en savoir plus sur ces mécanismes et les services tiers, consultez notre Politique des cookies."
      ]]
    ]
  },
  terms: {
    eyebrow: "Utilisation du service",
    title: "Conditions d'utilisation",
    intro: "Les règles essentielles pour consulter nos informations footballistiques, utiliser votre compte et accéder aux fonctionnalités disponibles.",
    sections: [
      ["Le service ProScore Live", [
        "ProScore Live rassemble des rencontres, des compétitions, des scores, des statistiques et des analyses footballistiques. Une partie des contenus est accessible librement ; d'autres fonctionnalités nécessitent une connexion et un droit d'accès actif.",
        "La disponibilité de chaque rencontre et de chaque analyse dépend notamment des données accessibles au moment de la consultation."
      ]],
      ["Des analyses, pas des certitudes", [
        "Les scénarios, pourcentages de confiance et projections sont des estimations. Ils peuvent changer, être incomplets ou se révéler inexacts. Aucun résultat sportif, score ou événement de match n'est garanti.",
        "Les informations sont proposées à titre informatif. Elles ne remplacent pas votre propre appréciation de leur fiabilité et de leurs limites."
      ]],
      ["Votre compte et sa sécurité", [
        "Vous êtes responsable de l'utilisation de votre compte et de la confidentialité de vos identifiants. Il est interdit de contourner les contrôles d'accès, de compromettre le service ou d'exploiter les données sans autorisation.",
        "La page Compte permet de se connecter et, lorsque nécessaire, de demander la récupération du mot de passe."
      ]],
      ["Les offres et les droits d'accès", [
        "Les différentes formules peuvent donner accès à un match, à une période déterminée ou à des fonctionnalités Sélection Pro, selon l'offre présentée. Les droits sont vérifiés côté serveur et restent valables uniquement pendant la période applicable.",
        "Les offres prévues fonctionnent sans renouvellement automatique. Les modalités de paiement et les conditions commerciales applicables seront affichées avant l'activation des ventes."
      ]],
      ["Paiements et remboursements", [
        "Aucun paiement ne doit être considéré comme confirmé tant qu'il n'a pas été vérifié par le service. Les moyens d'encaissement réellement activés, les tarifs, la durée d'accès et les éventuelles conditions de remboursement devront être clairement indiqués avant tout achat.",
        "La consultation d'une page d'offres n'entraîne pas, à elle seule, une facturation."
      ]],
      ["Disponibilité et propriété des contenus", [
        "Nous pouvons faire évoluer les fonctionnalités, corriger des erreurs ou interrompre temporairement l'accès pour des raisons techniques. Les noms, emblèmes et données provenant de tiers restent soumis aux droits de leurs titulaires.",
        "La présence d'une équipe, d'une compétition ou d'une cote ne signifie pas l'existence d'un partenariat officiel avec leurs titulaires."
      ]]
    ]
  },
  legal: {
    eyebrow: "Informations sur l'éditeur",
    title: "Mentions légales",
    intro: "Les informations relatives à ProScore Live, à son fonctionnement et à l'éditeur du service.",
    sections: [
      ["Le service", [
        "ProScore Live est une application web d'informations, de statistiques et d'analyses footballistiques. Les contenus et projections sont fournis à titre informatif et ne garantissent aucun résultat."
      ]],
      ["Éditeur", [
        "ProScore Live est le nom du service. L'identité légale de son éditeur, son adresse professionnelle et ses éventuelles références d'immatriculation seront indiquées ici avant l'ouverture officielle au public.",
        "Aucune dénomination sociale ni immatriculation n'est présentée comme acquise à ce stade."
      ]],
      ["Hébergement", [
        "La version actuelle est accessible sur un environnement de prévisualisation Emergent. Les informations légales de l'hébergeur du site public seront affichées dès que l'infrastructure et le domaine de production auront été arrêtés."
      ]],
      ["Contact", [
        "La page Contact et assistance réunit les réponses aux questions fréquentes et les moyens de contact lorsqu'ils sont activés. Un contact professionnel direct sera indiqué avant l'ouverture commerciale."
      ]],
      ["Données et contenus de tiers", [
        "Les logos, noms de clubs, noms de compétitions et autres éléments protégés appartiennent à leurs ayants droit respectifs. Leur utilisation dans l'application ne constitue pas une déclaration de partenariat ou d'affiliation officielle."
      ]],
      ["Limites de responsabilité", [
        "Malgré les contrôles effectués, les résultats, statistiques et projections peuvent être incomplets, retardés ou incorrects. Ils ne constituent pas une promesse de performance ni une garantie de résultat sportif."
      ]]
    ]
  },
  cookies: {
    eyebrow: "Votre navigation",
    title: "Cookies et stockage local",
    intro: "Des informations techniques peuvent être conservées par votre navigateur pour vous connecter et utiliser les fonctionnalités demandées.",
    sections: [
      ["Cookies et stockage local : quelle différence ?", [
        "Les cookies sont de petites informations associées à un site. Le navigateur dispose aussi de mécanismes comme localStorage et sessionStorage, qui peuvent conserver des données sans utiliser de cookies.",
        "Ces mécanismes ne servent pas tous au suivi publicitaire. Leur usage dépend de la fonction qu'ils remplissent."
      ]],
      ["Votre connexion", [
        "ProScore Live utilise Supabase pour l'authentification. Le navigateur peut conserver localement les éléments de session nécessaires à la connexion et à son maintien. Ces éléments permettent notamment d'éviter une reconnexion systématique à chaque navigation.",
        "Supprimer ces informations peut vous déconnecter du service."
      ]],
      ["Récupération du mot de passe", [
        "Un indicateur temporaire est enregistré dans sessionStorage pendant le parcours de récupération du mot de passe. Il permet de suivre l'étape en cours dans l'onglet du navigateur.",
        "Cet indicateur n'est pas destiné à personnaliser de la publicité."
      ]],
      ["Connexion avec Google", [
        "Lorsque vous choisissez « Continuer avec Google », vous êtes redirigé vers le service de connexion Google. Google peut alors utiliser ses propres cookies et mécanismes de sécurité, conformément à ses règles et paramètres.",
        "Cette redirection n'est déclenchée que lorsque vous choisissez cette méthode de connexion."
      ]],
      ["Traceurs facultatifs", [
        "Les mécanismes strictement nécessaires à un service demandé ne requièrent pas toujours un consentement préalable, selon les règles applicables. Les traceurs facultatifs, notamment publicitaires, peuvent en revanche nécessiter un accord avant leur activation.",
        "Si de tels traceurs sont ajoutés à ProScore Live, leur finalité et les choix proposés aux utilisateurs devront être présentés avant leur utilisation."
      ]],
      ["Gérer les données enregistrées", [
        "Vous pouvez consulter ou effacer les cookies et données de site depuis les paramètres de votre navigateur. Vous pouvez également vous déconnecter de ProScore Live depuis la page Compte.",
        "La suppression de certaines données locales peut interrompre votre session et nécessiter une nouvelle connexion."
      ]]
    ]
  }
};

const navLinks = [
  ["/confidentialite", "Confidentialité"],
  ["/conditions-utilisation", "Conditions d'utilisation"],
  ["/mentions-legales", "Mentions légales"],
  ["/cookies", "Cookies"],
  ["/assistance", "Assistance"],
];

export function LegalFooter() {
  return (
    <nav className="ps-l11b-footer" aria-label="Documents et assistance">
      {navLinks.map(([path, label]) => (
        <NavLink to={path} key={path} className={({ isActive }) => isActive ? "ps-l11d-active" : ""}>{label}</NavLink>
      ))}
    </nav>
  );
}

export default function LegalPage({ type }) {
  const page = pages[type] || pages.privacy;
  return (
    <div className="ps-l11b-page ps-l11d-page">
      <div className="ps-l11b-glow" aria-hidden="true" />
      <div className="ps-l11b-content">
        <Link className="ps-l11b-back" to="/">← Retour à l'accueil</Link>
        <header className="ps-l11d-heading">
          <div className="ps-l11b-label">PROSCORE LIVE <span aria-hidden="true">/</span> {page.eyebrow}</div>
          <h1>{page.title}</h1>
          <p className="ps-l11b-intro">{page.intro}</p>
          <p className="ps-l11d-date">Mise à jour : {updated}</p>
        </header>
        <div className="ps-l11b-sections">
          {page.sections.map(([title, paragraphs], index) => (
            <section className="ps-l11b-card" key={title} style={{ "--delay": `${Math.min(index, 5) * 55}ms` }}>
              <h2>{title}</h2>
              {paragraphs.map((paragraph, i) => <p key={i}>{paragraph}</p>)}
            </section>
          ))}
        </div>
        <div className="ps-l11d-bottom-help">
          <span>Une question sur ces informations ?</span>
          <Link to="/assistance">Consulter l'assistance <span aria-hidden="true">→</span></Link>
        </div>
      </div>
    </div>
  );
}
